/** @format */

import {
  setAdvancedQuestion,
  setBasicQuestion,
  setIntermediateQuestion,
} from "../../redux/action/interviewQuestions";
import apis from "../../utils/apis"; // Replace with your API library import

export function fetchInterviewQuestions(
  dispatch,
  skill,
  advancedQuestionsStore,
  intermediateQuestionsStore,
  basicQuestionsStore,
  setAdvancedQuestions,
  setIntermediateQuestions,
  setBasicQuestions,
  setInterviewQuestions,
  setInterviewQuestionsLoading,
  setAdvancedQuestionsLoading,
  setIntermediateQuestionsLoading,
  setBasicQuestionsLoading,
  selectedTab
) {
  const levels = {
    basic: "get-interviewQuestion-basic",
    intermediate: "get-interviewQuestion-intermediate",
    advanced: "get-interviewQuestion-advanced",
  };

  const MAX_RETRIES = 3;

  const fetchLevelData = async (
    levelKey,
    setLoading,
    setQuestionData,
    setQuestion,
    retries = 0
  ) => {
    if (skill?.name != undefined && skill?.name != null && skill?.name != "") {
      setLoading(true);
      try {
        const response = await apis.GET({
          endpoint: `/${levels[levelKey]}?skill=${skill?.name}`,
          options: { type: "json" },
        });
        // console.log("Skill", skill?.name);

        const data = response.data.questions;
        // console.log(`✅ Data fetched successfully for ${levelKey}:`, data);

        setInterviewQuestions((prev) => ({ ...prev, [levelKey]: data || [] }));
        dispatch(setQuestion(data));
        setQuestionData(data);
      } catch (error) {
        console.error(`❌ API call failed for ${levelKey}:`, error);

        if (
          (error?.response?.status === 504 ||
            error?.response?.data?.status === 400) &&
          retries < MAX_RETRIES
        ) {
          // console.log(`🔄 Retrying API call for ${levelKey}... Attempt ${retries + 1}`);
          return fetchLevelData(levelKey, setLoading, retries + 1);
        } else {
          console.error(
            `🚨 Non-retryable error or retry limit reached for ${levelKey}.`,
          );
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchQuestionsByTab = async () => {
    setInterviewQuestionsLoading(true);
    // console.log("basicQuestionsStore", basicQuestionsStore?.length);
    // console.log("selectedTab", selectedTab);

    if (selectedTab === "advanced") {
      if (advancedQuestionsStore?.length === 0) {
        // console.log("advancedQuestionsStore-called");
        await fetchLevelData(
          "advanced",
          setAdvancedQuestionsLoading,
          setAdvancedQuestions,
          setAdvancedQuestion
        );
      }
    } else if (selectedTab === "intermediate") {
      if (intermediateQuestionsStore?.length === 0) {
        await fetchLevelData(
          "intermediate",
          setIntermediateQuestionsLoading,
          setIntermediateQuestions,
          setIntermediateQuestion
        );
      }
    } else if (selectedTab === "basic") {
      if (basicQuestionsStore?.length === 0) {
        await fetchLevelData(
          "basic",
          setBasicQuestionsLoading,
          setBasicQuestions,
          setBasicQuestion
        );
      }
    } else {
      console.warn(`⚠️ Unknown selectedTab: ${selectedTab}`);
    }

    setInterviewQuestionsLoading(false);
  };

  fetchQuestionsByTab(); // Call the function based on selectedTab
}

export const fetchDisruptCareerData = (
  item,
  relevanceResult,
  setDisruptCareerData,
  setDisruptCareerLoading
) => {
  const MAX_RETRIES = 3;

  const skillParam = encodeURIComponent(item?.name);
  const idParam = encodeURIComponent(relevanceResult?.relevanceResultId);

  const endpoint = `/get-disrupt-career?skill=${skillParam}&id=${idParam}`;

  const fetchDisruptCareerData = async (retries = 0) => {
    try {
      setDisruptCareerLoading(true);
      const response = await apis.GET({ endpoint });
      
      // Handle different response structures
      // The response might be: { content: "[{...}]" } where content is a JSON string
      // Or it might be: { content: [{...}] } where content is already an array
      // Or it might be: [{...}] directly
      let parsedContent = response.data;
      
      // If response.data has a content property, use that
      if (response.data?.content !== undefined) {
        parsedContent = response.data.content;
      }
      
      // If content is a string, try to parse it as JSON
      if (typeof parsedContent === 'string') {
        try {
          // Check if it looks like JSON (starts with [ or {)
          const trimmed = parsedContent.trim();
          if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
            parsedContent = JSON.parse(parsedContent);
          }
          // Otherwise, keep it as string
        } catch (e) {
          // If parsing fails, use the string as-is
          console.warn('Failed to parse content as JSON, using as string:', e);
        }
      }
      console.log(parsedContent, "parsedContent")
      
      // If content is already an array or object, use it directly
      // console.log('Parsed disrupt career content:', parsedContent, 'Type:', typeof parsedContent, 'IsArray:', Array.isArray(parsedContent));
      setDisruptCareerData(parsedContent);
    } catch (error) {
      console.error(`❌ API call failed for get-disrupt-career:`, error);

      if (
        (error?.response?.status === 504 ||
          error?.response?.data?.status === 400) &&
        retries < MAX_RETRIES
      ) {
        return fetchDisruptCareerData(retries + 1);
      } else {
        console.error(
          `🚨 Non-retryable error or retry limit reached for get-disrupt-career.`
        );
      }
    } finally {
      setDisruptCareerLoading(false);
    }
  };

  fetchDisruptCareerData();
};

export const fetchTopSkillsInterviewQuestions = async (
  topSkills,
  setTopSkillsQuestions,
  setTopSkillsQuestionsLoading,
  setTopSkillsQuestionsError
) => {
  const MAX_RETRIES = 3;

  // Helper function to check if response is empty
  const isEmptyResponse = (data) => {
    // Check if data is null, undefined, or empty
    if (!data) return true;
    
    // Check if it's an empty object {}
    if (typeof data === "object" && !Array.isArray(data)) {
      const keys = Object.keys(data);
      if (keys.length === 0) return true;
      
      // Check if interviewQuestions exists but is empty
      if (data.interviewQuestions !== undefined) {
        if (!Array.isArray(data.interviewQuestions) || data.interviewQuestions.length === 0) {
          return true;
        }
      }
    }
    
    // Check if data is an empty array
    if (Array.isArray(data) && data.length === 0) {
      return true;
    }
    
    return false;
  };

  const fetchTopSkillsQuestions = async (retries = 0) => {
    try {
      setTopSkillsQuestionsLoading(true);
      setTopSkillsQuestionsError(false);

      const payload = {
        topSkills: topSkills.map((skill) => ({ name: skill.name })),
      };

      const response = await apis.POST({
        endpoint: "/get-top-skills-interviewQuestion",
        payload: payload,
        options: { type: "json" },
      });
      
      // console.log(response.data, "response.data from get-top-skills-interviewQuestion");
      
      // Check if response is empty object or has no interview questions
      if (isEmptyResponse(response.data)) {
        
        
        if (retries < MAX_RETRIES) {
          // Retry if we haven't reached max retries
          return fetchTopSkillsQuestions(retries + 1);
        } else {
          // After max retries, set error and don't show component
          
          setTopSkillsQuestionsError(true);
          setTopSkillsQuestions(null);
          return;
        }
      }
      
      // Valid response received
      // console.log(response.data.interviewQuestions, "response.data.interviewQuestions");
      setTopSkillsQuestions(response.data || []);
      setTopSkillsQuestionsError(false);
    } catch (error) {
      console.error(
        `❌ API call failed for get-top-skills-interviewQuestion:`,
        error
      );

      if (
        (error?.response?.status === 504 ||
          error?.response?.data?.status === 400) &&
        retries < MAX_RETRIES
      ) {
        return fetchTopSkillsQuestions(retries + 1);
      } else {
        console.error(
          `🚨 Non-retryable error or retry limit reached for get-top-skills-interviewQuestion.`
        );
        setTopSkillsQuestionsError(true);
      }
    } finally {
      setTopSkillsQuestionsLoading(false);
    }
  };

  fetchTopSkillsQuestions();
};

